import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { loadReferenceLetters } from "../../services/referenceLetterService";
import {
  evaluateOrderStatus,
  GetAllGeneralOrderDocuments,
  getFullOrder,
  updateOrder,
  updateOrderStatus,
} from "../../services/orderService";
import { ReferenceLetter } from "../../interfaces/referenceLetter";
import { HttpService } from "../../services/httpService";
import { getClient } from "../../services/clientService";
import {
  formatDate,
  getSafePickerDate,
  getStates,
  sortCriminalDetails,
  sortOrderServices,
  toSafeDateString,
} from "../../services/helpers";
import { getInvoiceProcessTypes } from "../../services/invoiceProcessTypesService";
import { getReferenceTypes } from "../../services/referenceTypeService";
import {
  createReferenceOrder,
  getCandidateNotifiedDatesByReferenceOrderID,
  updateReferenceOrder,
} from "../../services/referenceOrderService";
import { getVendors } from "../../services/vendorService";
import ToolTip from "../../components/component-tools/ToolTip";
import CompleteOrder from "../../components/order-components/CompleteOrder";
import UploadCandidateForm from "../../components/order-components/UploadCandidateForm";
import DeleteOrder from "../../components/order-components/DeleteOrder";
import {
  deleteAdditionalName,
  updateAdditionalName,
} from "../../services/additionalNameService";
import AddAdditionalName from "../../components/order-components/AddAdditionalName";
import { AdditionalName } from "../../interfaces/additionalName";
import {
  createCriminalOrder,
  updateCriminalOrder,
} from "../../services/criminalOrderService";
import NotifyClient from "../../components/order-components/NotifyClient";
import DownloadCandidateForm from "../../components/order-components/DownloadCandidateForm";
import ConfirmDelete from "../../components/component-tools/ConfirmDelete";
import UploadClientResponse from "../../components/order-components/UploadClientResponse";
import DownloadClientResponse from "../../components/order-components/DownloadClientResponse";
import UploadGeneralOrderDoc from "../../components/order-components/UploadGeneralOrderDoc";
import DownloadGeneralOrderDocs from "../../components/order-components/DownloadGeneralOrderDocs";
import MonthDayYearPicker from "../../components/component-tools/MonthDayYearPicker";
import { useAllOrdersContext } from "../../contexts/AllOrdersContext";
import { useAdminReviewContext } from "../../contexts/AdminReviewContext";
import { useInProgressContext } from "../../contexts/InProgressContext";
import { useCompletedOrdersContext } from "../../contexts/CompletedOrdersContext";
import { useDeletedOrdersContext } from "../../contexts/DeletedOrdersContext";
import { usePendingOrdersContext } from "../../contexts/PendingOrdersContext";
import { useViewOrderContext } from "../../contexts/ViewOrderContext";
import {
  checkForOpenSubOrder,
  createSubscriptionOrder,
  updateSubscriptionOrder,
} from "../../services/subscriptionOrderService";
import { NotificationType } from "../../interfaces/clientNotification";
import SubscriptionOrderSection from "./view-order/SubscriptionOrderSection";
import CriminalOrderSection from "./view-order/CriminalOrderSection";
import ReferenceOrderSection from "./view-order/ReferenceOrderSection";
import AddRequiredForm from "../../components/order-components/AddRequiredForm";
import { deleteOrderForm } from "../../services/orderFormService";
import HardDeleteOrder from "../../components/order-components/HardDeleteOrder";
import { Candidate } from "../../interfaces/candidate";

const AdminViewOrder = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const { orderId } = useParams();
  const {
    order,
    setOrder,
    orderForms,
    setOrderForms,
    orderFormTypes,
    setOrderFormTypes,
    criminalOrder,
    setCriminalOrder,
    setCriminalDetails,
    subscriptionOrder,
    setSubscriptionOrder,
    setSubscriptionDetails,
    referenceOrder,
    setReferenceOrder,
    referenceDetails,
    setReferenceDetails,
    services,
    setServices,
    client,
    setClient,
    additionalNames,
    setAdditionalNames,
    invoiceProcessTypes,
    setInvoiceProcessTypes,
    referenceTypes,
    setReferenceTypes,
    orderServices,
    setOrderServices,
    setVendors,
    signedUrls,
    setSignedUrls,
    setCriminalFinalUrl,
    setReferenceFinalUrl,
    deleteDisabled,
    setDeleteDisabled,
    setCanSendCriminalFinal,
    setCandidateNotifiedDates,
  } = useViewOrderContext();
  const [allLetters, setAllLetters] = useState<
    Record<number, ReferenceLetter[]>
  >({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const location = useLocation();
  const [successMessage, setSuccessMessage] = useState("");
  const states = getStates();
  const navigate = useNavigate();
  const [showChangeStatus, setShowChangeStatus] = useState(false);
  const [showCompleteOrder, setShowCompleteOrder] = useState(false);
  const [showUploadCandidateFormPopup, setShowUploadCandidateFormPopup] =
    useState(false);
  const [showDownloadCandidateFormPopup, setShowDownloadCandidateFormPopup] =
    useState(false);
  const [showAddOrderFormPopup, setShowAddOrderFormPopup] = useState(false);
  const [showDeleteOrder, setShowDeleteOrder] = useState(false);
  const [showHardDeleteOrder, setShowHardDeleteOrder] = useState(false);
  const [completedDisabled, setCompleteDisabled] = useState(false);
  const [showAddAdditionalName, setShowAddAdditionalName] = useState(false);
  const [showNotifyClientPopup, setShowNotifyClientPopup] = useState(false);
  const [showNotifyClientOrderPopup, setShowNotifyClientOrderPopup] =
    useState(false);
  const [deletingNameID, setDeletingNameID] = useState<number | null>(null);
  const [showUploadResponsePopup, setShowUploadResponsePopup] = useState(false);
  const [showDownloadResponsePopup, setShowDownloadResponsePopup] =
    useState(false);
  const [showUploadGeneralOrderDocPopup, setShowUploadGeneralOrderDocPopup] =
    useState(false);
  const [
    showDownloadGeneralOrderDocsPopup,
    setShowDownloadGeneralOrderDocsPopup,
  ] = useState(false);
  const { updateOrderInAllList } = useAllOrdersContext();
  const { updateOrderInAdminReviewList } = useAdminReviewContext();
  const { updateOrderInInProgressList } = useInProgressContext();
  const { updateOrderInCompletedOrdersList } = useCompletedOrdersContext();
  const { updateOrderInDeletedOrdersList } = useDeletedOrdersContext();
  const { updateOrderInPendingOrdersList } = usePendingOrdersContext();
  const source = (location.state as { source?: string })?.source ?? "all";
  const [orderDocCount, setOrderDocCount] = useState(0);
  const [openSubOrder, setOpenSubOrder] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);
  const [doublePayment, setDoublePayment] = useState(false);
  const [candidate, setCandidate] = useState<Candidate>();

  const containerMap: Record<number, string> = useMemo(
    () => ({
      1: "background-check-agreements",
      2: "criminal-records-authorizations",
      3: "credit-report-authorizations",
      4: "motor-vehicle-authorizations",
      5: "reference-screening-authorizations",
      6: "candidate-screening-questionnaires",
      7: "reference-identification-forms",
      8: "transfer-releases",
      9: "social-media-authorizations",
    }),
    [],
  );

  const statusClassMap: Record<number, string> = {
    1: "status-pending",
    2: "status-admin-review",
    3: "status-in-process",
    4: "status-completed",
    5: "status-deleted",
    6: "status-subscription",
  };

  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        if (!orderId) return;
        const numericOrderId = parseInt(orderId, 10);

        if (order?.orderID === numericOrderId) {
          setLoading(false);
          return;
        }

        const fullOrder = await getFullOrder(numericOrderId);

        setOrder(fullOrder.order);
        setCandidate(fullOrder.candidate);
        setAdditionalNames(fullOrder.additionalNames || []);
        setOrderForms(fullOrder.orderForms || []);
        setOrderFormTypes(fullOrder.orderFormTypes);
        setCriminalOrder(fullOrder.criminalOrder);
        setCriminalDetails(fullOrder.criminalDetails || []);
        setSubscriptionOrder(fullOrder.subscriptionOrder);
        setSubscriptionDetails(fullOrder.subscriptionDetails || []);
        setReferenceOrder(fullOrder.referenceOrder);
        setReferenceDetails(fullOrder.referenceDetails || []);
        setServices(fullOrder.services);
        setOrderServices(fullOrder.orderServices || []);
        setDoublePayment(fullOrder.doublePayment);

        const sortedCriminalDetails = await sortCriminalDetails(
          fullOrder.criminalDetails,
        );
        setCriminalDetails(sortedCriminalDetails);

        const clientData = await getClient(fullOrder.order.clientID);
        setClient(clientData);

        //get the urls
        if (fullOrder.orderForms && fullOrder.orderForms.length > 0) {
          const urlPromises = fullOrder.orderForms.map(async (form) => {
            const container = containerMap[form.orderFormTypeID];
            if (
              container &&
              fullOrder.order &&
              form.completedFormUrl !== "" &&
              form.completedFormUrl !== null
            ) {
              try {
                const signedUrl = await HttpService.getFileAdmin(
                  container,
                  form.orderFormID.toString(),
                  form.completedFormUrl,
                );
                return { id: form.orderFormID, signedUrl };
              } catch (error) {
                console.error(
                  `Failed to fetch signed URL for form ID ${form.orderFormID}:`,
                  error,
                );
                return { id: form.orderFormID, signedUrl: null };
              }
            } else {
              console.warn(
                `No container mapping for form ID ${form.orderFormID}`,
              );
              return { id: form.orderFormID, signedUrl: null };
            }
          });
          const urlResults = await Promise.all(urlPromises);
          const urlMap: Record<number, string> = Object.fromEntries(
            urlResults
              .filter(({ signedUrl }) => signedUrl !== null)
              .map(({ id, signedUrl }) => [Number(id), signedUrl as string]),
          );
          setSignedUrls(urlMap);
        }

        //get final report urls
        try {
          if (
            fullOrder.criminalOrder &&
            fullOrder.criminalOrder.finalReportUrl
          ) {
            const signedCriminalUrl = await HttpService.getFileAdmin(
              "criminal-final-reports",
              fullOrder.criminalOrder.criminalOrderID.toString(),
              fullOrder.criminalOrder.finalReportUrl,
            );
            setCriminalFinalUrl(signedCriminalUrl);
          }
          if (
            fullOrder.referenceOrder &&
            fullOrder.referenceOrder.finalReportUrl
          ) {
            const signedReferenceUrl = await HttpService.getFileAdmin(
              "reference-final-reports",
              fullOrder.referenceOrder.referenceOrderID.toString(),
              fullOrder.referenceOrder.finalReportUrl,
            );
            setReferenceFinalUrl(signedReferenceUrl);
          }
        } catch (error) {
          console.error(
            `Failed to fetch signed URL for final report(s)`,
            error,
          );
        }

        // get process types
        try {
          const types = await getInvoiceProcessTypes();
          setInvoiceProcessTypes(types);
        } catch (error) {
          console.error("Error fetching invoice process types:", error);
        }

        // get reference types
        try {
          let typesR = await getReferenceTypes();
          typesR = typesR.filter((t) => t.referenceTypeID !== 2);
          setReferenceTypes(typesR);
        } catch (error) {
          console.error("Error fetching reference types:", error);
        }

        // get vendors
        try {
          const vendorsR = await getVendors();
          setVendors(vendorsR);
        } catch (error) {
          console.error("Error fetching vendors:", error);
        }

        //set disabled fields
        if (
          (fullOrder.referenceOrder &&
            fullOrder.referenceOrder?.referenceOrderID !== 0 &&
            fullOrder.referenceOrder.finalReportUrl === null) ||
          (fullOrder.criminalOrder &&
            fullOrder.criminalOrder?.criminalOrderID !== 0 &&
            fullOrder.criminalOrder.finalReportUrl === null)
        ) {
          setCompleteDisabled(true);
        }

        const hasReferenceOrderValues =
          fullOrder.referenceOrder &&
          fullOrder.referenceOrder.finalReportUrl &&
          fullOrder.referenceOrder.finalReportUrl.trim() !== "";

        const hasCriminalOrderValues =
          fullOrder.criminalOrder &&
          fullOrder.criminalOrder.finalReportUrl &&
          fullOrder.criminalOrder.finalReportUrl.trim() !== "";

        if (fullOrder.order.orderStatusID === 5) {
          setDeleteDisabled(false);
        } else if (
          hasReferenceOrderValues ||
          hasCriminalOrderValues ||
          fullOrder.order.orderStatusID !== 1
        ) {
          setDeleteDisabled(true);
        } else {
          setDeleteDisabled(false);
        }

        const standardDetails = fullOrder.criminalDetails.filter(
          (c) => !c.subscriptionAddOn,
        );

        // It can send IF there is at least one standard detail AND they are all complete/inactive
        const canSend =
          standardDetails.length > 0 &&
          standardDetails.every((c) => c.receivedDate || c.inActive);

        setCanSendCriminalFinal(canSend);

        if (
          fullOrder.referenceOrder &&
          fullOrder.referenceOrder.referenceOrderID !== 0
        ) {
          const candidateDatesResponse =
            await getCandidateNotifiedDatesByReferenceOrderID(
              fullOrder.referenceOrder.referenceOrderID,
            );
          setCandidateNotifiedDates(candidateDatesResponse);
        }

        const data = await GetAllGeneralOrderDocuments(fullOrder.order.orderID);
        setOrderDocCount(data.length);

        // look for any other orders with matching DOB and last 4 of SSN that has a status of 6
        const openResult = await checkForOpenSubOrder(fullOrder.order.orderID);
        setOpenSubOrder(openResult);
      } catch (error) {
        console.error("Error fetching client:", error);
        setErrorMessage("Failed to load order data.");
      } finally {
        setLoading(false);
        const scrollY = localStorage.getItem("scrollY");
        if (scrollY) {
          setTimeout(() => {
            window.scrollTo(0, parseInt(scrollY, 10));
            localStorage.removeItem("scrollY");
          }, 0);
        }
      }
    };

    fetchOrderData();
  }, [orderId, containerMap]);

  const updateDocCount = async () => {
    const data = await GetAllGeneralOrderDocuments(order.orderID);
    setOrderDocCount(data.length);
  };

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.successMessage, location.pathname, navigate]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    const fetchReferenceLetters = async () => {
      try {
        if (referenceDetails.length > 0) {
          const lettersMap = await loadReferenceLetters(referenceDetails);
          setAllLetters(lettersMap);
        }
      } catch (error) {
        console.error("Error fetching reference letters:", error);
      }
    };

    fetchReferenceLetters();
  }, [referenceDetails]);

  const getServiceNames = (type: number): string[] => {
    const sorted = sortOrderServices(orderServices);

    return sorted
      .map((orderService) => {
        const service = services.find(
          (s) => s.serviceID === orderService.serviceID,
        );
        if (!service) {
          console.warn(
            `Service not found for ServiceID: ${orderService.serviceID}`,
          );
        }
        return service;
      })
      .filter((service) => {
        if (!service) return false;

        const isReferenceService = service.referenceYears !== null;
        const isSubscriptionService = service.subscriptionService === true;

        if (type === 1) {
          return !isReferenceService && !isSubscriptionService;
        } else if (type === 2) {
          return isReferenceService;
        } else if (type === 3) {
          return isSubscriptionService;
        }

        return false;
      })
      .map((service) => service!.serviceName);
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
    type?: string,
  ) => {
    setEditing(true);
    let value: string | boolean | number =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (field === "referenceTypeID") {
      value = Number(value);
    }

    if (type !== "" && type === "criminal") {
      setCriminalOrder((prev) => ({
        ...prev!,
        [field]: value,
      }));
    } else if (order && field in order) {
      setOrder((prev) => ({
        ...prev,
        [field]: value,
      }));
    } else if (
      subscriptionOrder &&
      field in subscriptionOrder &&
      type === "subscription"
    ) {
      setSubscriptionOrder((prev) => ({
        ...prev!,
        [field]: value,
      }));
    } else if (referenceOrder && field in referenceOrder) {
      setReferenceOrder((prev) => ({
        ...prev!,
        [field]: value,
      }));
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (order) {
        if (order.candidateState !== "INT") {
          order.candidateCountry = "United States";
        }
        await updateOrder(order);
        setEditing(false);
        setSuccessMessage("Order information updated successfully.");
      }
      if (referenceOrder?.referenceOrderID !== 0 && referenceOrder) {
        await updateReferenceOrder(referenceOrder);
        setEditing(false);
        setSuccessMessage("Order information updated successfully.");
      }
      if (criminalOrder?.criminalOrderID !== 0 && criminalOrder) {
        await updateCriminalOrder(criminalOrder);
        setEditing(false);
        setSuccessMessage("Order information updated successfully.");
      }
      if (subscriptionOrder?.subscriptionOrderID !== 0 && subscriptionOrder) {
        await updateSubscriptionOrder(subscriptionOrder);
        setEditing(false);
        setSuccessMessage("Order information updated successfully.");
      }

      await evaluateOrderStatus(order.orderID);
    } catch (error) {
      console.error("Error updating:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleStatusChange = async (statusID: number) => {
    try {
      if (statusID && order) {
        setShowChangeStatus(false);
        setLoading(true);

        await updateOrderStatus(order.orderID, statusID);

        setOrder((prev) =>
          prev ? { ...prev, orderStatusID: statusID } : prev,
        );
        setSuccessMessage("Order status updated successfully.");
      } else {
        setErrorMessage("Invalid status ID or order not available.");
      }
    } catch (error) {
      console.error("Error updating order status:", error);
    } finally {
      handleUpdateContext();
      setLoading(false);
    }
  };

  const handleUpdateContext = () => {
    switch (source) {
      case "pending":
        updateOrderInPendingOrdersList(order, order.orderStatusID);
        break;
      case "adminReview":
        updateOrderInAdminReviewList(order, order.orderStatusID);
        break;
      case "inProgress":
        updateOrderInInProgressList(order, order.orderStatusID);
        break;
      case "completed":
        updateOrderInCompletedOrdersList(order, order.orderStatusID);
        break;
      case "deleted":
        updateOrderInDeletedOrdersList(order, order.orderStatusID);
        break;
      default:
        updateOrderInAllList(order);
        break;
    }
  };

  const handleAdditionalNameChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: keyof AdditionalName,
    index: number,
  ) => {
    const { value } = e.target;

    setAdditionalNames((prevNames) =>
      prevNames.map((name, i) =>
        i === index ? { ...name, [field]: value } : name,
      ),
    );
  };

  const handleDeleteAdditionalName = async (additionalNameID: number) => {
    try {
      await deleteAdditionalName(additionalNameID);

      setAdditionalNames((prevNames) =>
        prevNames.filter((name) => name.additionalNameID !== additionalNameID),
      );
      setSuccessMessage("Additional Name Deleted.");
    } catch (error) {
      console.error("Error deleting additional name:", error);
      setSuccessMessage("");
    }
  };

  const handleUpdateAdditionalName = async (
    additionalNameID: number,
    updatedAdditionalName: AdditionalName,
  ) => {
    try {
      await updateAdditionalName(additionalNameID, updatedAdditionalName);

      setAdditionalNames((prevNames) =>
        prevNames.map((name) =>
          name.additionalNameID === additionalNameID
            ? updatedAdditionalName
            : name,
        ),
      );
      setSuccessMessage("Additional Name Updated Successfully.");
    } catch (error) {
      console.error("Error updating additional name:", error);
      setSuccessMessage("");
    }
  };

  const handleNotifyClientClosePopup = (message?: string) => {
    if (message) {
      window.location.reload();
    }
    setShowNotifyClientPopup(false);
  };

  const handleNotifyClientOrderClosePopup = (message?: string) => {
    if (message) {
      window.location.reload();
    }
    setShowNotifyClientOrderPopup(false);
  };

  const handleCreateCriminalOrder = async () => {
    try {
      if (order) {
        setLoading(true);

        var response = await createCriminalOrder(order.orderID);

        setCriminalOrder(response);
        setSuccessMessage("Criminal Order created successfully.");
      } else {
        setErrorMessage("Order not available.");
      }
    } catch (error) {
      console.error("Error creating criminal order:", error);
    } finally {
      handleUpdateContext();
      setLoading(false);
    }
  };

  const handleCreateReferenceOrder = async () => {
    try {
      if (order) {
        setLoading(true);

        var response = await createReferenceOrder(order.orderID);

        setReferenceOrder(response);
        setSuccessMessage("Reference Order created successfully.");
      } else {
        setErrorMessage("Order not available.");
      }
    } catch (error) {
      console.error("Error creating reference order:", error);
    } finally {
      handleUpdateContext();
      setLoading(false);
    }
  };

  const handleCreateSubscriptionOrder = async () => {
    try {
      if (order) {
        setLoading(true);

        var response = await createSubscriptionOrder(order.orderID);

        setSubscriptionOrder(response);
        setSuccessMessage("Subscription Order created successfully.");
      } else {
        setErrorMessage("Order not available.");
      }
    } catch (error) {
      console.error("Error creating subscription order:", error);
    } finally {
      handleUpdateContext();
      setLoading(false);
    }
  };

  const handleDeleteOrderForm = async () => {
    try {
      setLoading(true);
      if (!deleteTarget) return;
      await deleteOrderForm(deleteTarget);

      setSuccessMessage("Form deleted successfully.");
      window.location.reload();
    } catch (error) {
      console.error("Error deleting form:", error);
      setErrorMessage("An error occurred while deleting the form.");
    } finally {
      setLoading(false);
    }
  };

  const renderFormButtons = () => {
    if (orderForms.length === 0) {
      return <div>*Migrated order, no order forms*</div>;
    }
    return orderForms.map((orderForm) => {
      const orderFormType = orderFormTypes.find(
        (type) => type.orderFormTypeID === orderForm.orderFormTypeID,
      );

      const signedUrl = signedUrls[orderForm.orderFormID];

      var warningClass = "";

      if (
        referenceOrder?.canQCrimAdmit &&
        orderFormType?.orderFormTypeID === 6
      ) {
        warningClass = "btn-red";
      }

      if (orderFormType && signedUrl) {
        return (
          <div
            style={{ display: "flex", height: "max-content" }}
            key={orderForm.orderFormID}
          >
            <button
              className={`smbtn ${warningClass}`}
              onClick={() => window.open(signedUrl, "_blank")}
            >
              {orderFormType.description || "No Description"}
              <i className="btn-icon bi bi-box-arrow-up-right"></i>
            </button>
            {/* <div onClick={() => setDeleteTarget(orderForm.orderFormID)}>
                <i
                  className="bi bi-x"
                  style={{ color: "red", cursor: "pointer" }}
                ></i>
              </div> */}
          </div>
        );
      }

      return (
        <div
          style={{ display: "flex", height: "max-content" }}
          key={orderForm.orderFormID}
        >
          <button className="smbtn" disabled>
            {orderFormType?.description}
          </button>
          <div onClick={() => setDeleteTarget(orderForm.orderFormID)}>
            <i
              className="bi bi-x"
              style={{ color: "red", cursor: "pointer" }}
            ></i>
          </div>
        </div>
      );
    });
  };

  return (
    <div className="content-container">
      {!loading && (
        <div className="header-container">
          <div
            className="page-items-container"
            style={{ alignItems: "center" }}
          >
            <h1>
              {(order.applican_link || order.candidat_link) && "*"}
              {order?.candidateFirstName} {order?.candidateLastName}
            </h1>
            <div
              className={`chip-date ${
                statusClassMap[order.orderStatusID] || "status-unknown"
              }`}
              style={{
                gap: "10px",
                justifyContent: "end",
                cursor:
                  order.orderStatusID === 2 ||
                  order.orderStatusID === 3 ||
                  order.orderStatusID === 1
                    ? "pointer"
                    : "default",
              }}
              onClick={() => {
                if (
                  order.orderStatusID === 2 ||
                  order.orderStatusID === 3 ||
                  order.orderStatusID === 1
                ) {
                  setShowChangeStatus(!showChangeStatus);
                }
              }}
            >
              <div>
                {order.orderStatusID === 2
                  ? "Admin Review"
                  : order.orderStatusID === 3
                    ? "In Process"
                    : order.orderStatusID === 4
                      ? "Completed"
                      : order.orderStatusID === 5
                        ? "Deleted"
                        : order.orderStatusID === 6
                          ? "Subscription"
                          : "Pending"}
              </div>
            </div>
          </div>
          <div
            className={
              order.orderStatusID === 2 || order.orderStatusID === 3
                ? `status-tag-active`
                : `status-tag-past`
            }
          >
            {order.orderStatusID === 2 || order.orderStatusID === 3
              ? "ACTIVE"
              : "PAST"}
          </div>
        </div>
      )}

      {/* Change Status Popup */}
      {showChangeStatus && (
        <>
          <div
            className="background-mask"
            onClick={() => setShowChangeStatus(false)}
          ></div>
          <div className="filter-popup">
            <div className="close-button-container">
              <button
                className="close-button"
                onClick={() => setShowChangeStatus(false)}
              >
                <i className="bi bi-x-circle"></i>
              </button>
            </div>
            <div className="filter-header">
              <h2>Change Order Status</h2>
              <div style={{ fontSize: "small" }}>
                Click the status you would like to change this order to.
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "center",
                marginTop: "20px",
                flexWrap: "wrap",
              }}
            >
              <div
                className="chip-date"
                style={{ cursor: "pointer" }}
                onClick={() => handleStatusChange(1)}
              >
                <div>{"Pending"}</div>
              </div>
              <div
                className="chip-date"
                style={{ cursor: "pointer" }}
                onClick={() => handleStatusChange(2)}
              >
                <div>{"Admin Review"}</div>
              </div>
              <div
                className="chip-date"
                style={{ cursor: "pointer" }}
                onClick={() => handleStatusChange(3)}
              >
                <div>{"In Process"}</div>
              </div>
              <div
                className="chip-date"
                style={{ cursor: "pointer" }}
                onClick={() => handleStatusChange(6)}
              >
                <div>{"Subscription"}</div>
              </div>
            </div>
          </div>
        </>
      )}

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          {openSubOrder && (
            <>
              <div className="error highlight-text">
                <b>
                  There is an active subscription service for this candidate
                  attached to another order.
                </b>
              </div>
              <br></br>
              <br></br>
            </>
          )}
          {doublePayment && <div className="error">DOUBLE PAYMENT FOUND.</div>}
          <div className="data-container">
            {order && (
              <>
                {order.orderStatusID === 5 && (
                  <div style={{ color: "red", fontSize: "large" }}>
                    <b>{order.deleteMessage}</b>
                  </div>
                )}

                <div
                  style={{
                    flex: "0 0 100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "20px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        textDecoration: "underline",
                        fontSize: "x-large",
                        alignItems: "start",
                      }}
                    >
                      Client
                      <div style={{ display: "flex", gap: "10px" }}>
                        <div>
                          <button
                            style={{ marginLeft: "20px" }}
                            className={`smbtn btn-grey ${
                              order.orderStatusID === 4 ||
                              order.orderStatusID === 5 ||
                              order.clientNotifiedDate !== null
                                ? "disabled"
                                : ""
                            }`}
                            onClick={() =>
                              setShowNotifyClientOrderPopup(
                                !showNotifyClientOrderPopup,
                              )
                            }
                            disabled={
                              order.orderStatusID === 4 ||
                              order.orderStatusID === 5 ||
                              order.clientNotifiedDate !== null
                            }
                          >
                            Notify Client{" "}
                            <i className="btn-icon bi bi-envelope"></i>
                          </button>
                        </div>
                        <ToolTip
                          tooltipText="Notify the Client that the Candidate has completed their forms and the order has started."
                          placement="right"
                          className="info-icon-spec"
                        ></ToolTip>
                      </div>
                      {order?.clientNotifiedDate !== null && (
                        <button
                          className="smbtn btn-grey"
                          style={{
                            cursor: "default",
                            background: "none",
                            color: "black",
                            paddingTop: "10px",
                          }}
                        >
                          {formatDate(order?.clientNotifiedDate, "us")}
                        </button>
                      )}
                    </div>
                    <div style={{ flex: "0 0 50%" }}>
                      <div className="field-long" style={{ flex: "0 0 100%" }}>
                        <div className="field-label">
                          <ToolTip
                            tooltipText={`${order.alert}\n${formatDate(order?.alertLastUpdatedDate, "datetime")}`}
                            placement="bottom"
                            className="alert-sticky"
                          >
                            <i className="bi bi-stickies-fill"></i>
                          </ToolTip>
                        </div>
                        <div className="field-input">
                          <input
                            className="form-control"
                            type="text"
                            value={order.alert ?? ""}
                            onChange={(e) => handleInputChange(e, "alert")}
                            maxLength={4000}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "20px",
                          fontSize: "14px",
                          marginTop: "4px",
                          justifyContent: "right",
                        }}
                      >
                        <div>
                          {formatDate(order?.alertLastUpdatedDate, "datetime")}
                        </div>
                        <div>{order.alert?.length ?? 0}/4000</div>
                      </div>
                    </div>
                  </div>
                  {showNotifyClientOrderPopup && order && (
                    <NotifyClient
                      orderId={order.orderID}
                      clientId={order.clientID}
                      candidateEmail={order.candidateEmail ?? ""}
                      type={NotificationType.Order}
                      onClose={handleNotifyClientOrderClosePopup}
                    />
                  )}
                  <div style={{ display: "flex", gap: "20px" }}>
                    <div className="field-stacked" style={{ flex: "0 0 40%" }}>
                      <label className="field-label">Client Name:</label>
                      <div className="field-input">
                        <input
                          className="form-control"
                          type="text"
                          value={client?.clientName}
                          disabled
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                      <label className="field-label">
                        Client Abbreviation/Number:
                      </label>
                      <div className="field-input">
                        <input
                          className="form-control"
                          type="text"
                          value={client?.abbreviation}
                          disabled
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className="text-container-header"
                  style={{ flex: "0 0 100%", display: "flex", gap: "10px" }}
                >
                  Candidate{" "}
                  <div className="field-input" style={{ width: "20%" }}>
                    <input
                      className={`form-control`}
                      type="text"
                      value={candidate?.candidateEmail ?? ""}
                      disabled={true}
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                  <label className="field-label">Title:</label>
                  <div className="field-input">
                    <input
                      className={`form-control`}
                      type="text"
                      value={order.candidateTitle ?? ""}
                      onChange={(e) => handleInputChange(e, "candidateTitle")}
                      disabled={
                        order.orderStatusID === 4 || order.orderStatusID === 5
                      }
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 25%" }}>
                  <label className="field-label">First Name:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={order.candidateFirstName ?? ""}
                      onChange={(e) =>
                        handleInputChange(e, "candidateFirstName")
                      }
                      disabled={
                        order.orderStatusID === 4 || order.orderStatusID === 5
                      }
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">Middle Name:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={order.candidateMI ? order.candidateMI : ""}
                      onChange={(e) => handleInputChange(e, "candidateMI")}
                      disabled={
                        order.orderStatusID === 4 || order.orderStatusID === 5
                      }
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 25%" }}>
                  <label className="field-label">Last Name:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={order.candidateLastName}
                      onChange={(e) =>
                        handleInputChange(e, "candidateLastName")
                      }
                      disabled={
                        order.orderStatusID === 4 || order.orderStatusID === 5
                      }
                    />
                  </div>
                </div>
                {referenceOrder && referenceOrder.referenceOrderID !== 0 && (
                  <div className="field-stacked" style={{ flex: "0 0 90%" }}>
                    <div style={{ display: "flex", gap: "5px" }}>
                      <label className="field-label">Full Name:</label>
                      <ToolTip
                        tooltipText="The Full Name field is used in both the Reference Questionnaire and the Reference Final Report."
                        placement="right"
                        className="info-icon-spec"
                      ></ToolTip>
                    </div>
                    <div className="field-input">
                      <input
                        className="form-control"
                        type="text"
                        value={referenceOrder?.fullName || ""}
                        onChange={(e) => handleInputChange(e, "fullName")}
                        disabled={
                          order.orderStatusID === 4 || order.orderStatusID === 5
                        }
                      />
                    </div>
                  </div>
                )}
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">SSN:</label>

                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={order.candidateSSN ? order.candidateSSN : ""}
                      onChange={(e) => handleInputChange(e, "candidateSSN")}
                      disabled={
                        order.orderStatusID === 4 || order.orderStatusID === 5
                      }
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">Canadian SIN:</label>

                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={order.canadianSIN ? order.canadianSIN : ""}
                      onChange={(e) => handleInputChange(e, "canadianSIN")}
                      disabled={
                        order.orderStatusID === 4 || order.orderStatusID === 5
                      }
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 30%" }}>
                  <label className="field-label">Phone:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={order.candidatePhone ? order.candidatePhone : ""}
                      onChange={(e) => handleInputChange(e, "candidatePhone")}
                      disabled={
                        order.orderStatusID === 4 || order.orderStatusID === 5
                      }
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 40%" }}>
                  <label className="field-label">DOB:</label>
                  <MonthDayYearPicker
                    value={getSafePickerDate(order.candidateDOB)}
                    onChange={(newDate) =>
                      handleInputChange(
                        { target: { value: toSafeDateString(newDate) } } as any,
                        "candidateDOB",
                      )
                    }
                    disabled={
                      order.orderStatusID === 4 || order.orderStatusID === 5
                    }
                  />
                </div>
                <div className="field-stacked" style={{ flex: "0 0 40%" }}>
                  <label className="field-label">Email:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={order.candidateEmail ? order.candidateEmail : ""}
                      onChange={(e) => handleInputChange(e, "candidateEmail")}
                      disabled={order.orderStatusID !== 1}
                    />
                  </div>
                </div>

                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">Address:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={
                        order.candidateAddress ? order.candidateAddress : ""
                      }
                      onChange={(e) => handleInputChange(e, "candidateAddress")}
                      disabled={
                        order.orderStatusID === 4 || order.orderStatusID === 5
                      }
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                  <label className="field-label">Address 2:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={
                        order.candidateAddress2 ? order.candidateAddress2 : ""
                      }
                      onChange={(e) =>
                        handleInputChange(e, "candidateAddress2")
                      }
                      disabled={
                        order.orderStatusID === 4 || order.orderStatusID === 5
                      }
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">City:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={order.candidateCity ? order.candidateCity : ""}
                      onChange={(e) => handleInputChange(e, "candidateCity")}
                      disabled={
                        order.orderStatusID === 4 || order.orderStatusID === 5
                      }
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">State:</label>
                  <select
                    className="form-select"
                    value={order.candidateState ? order.candidateState : ""}
                    onChange={(e) => handleInputChange(e, "candidateState")}
                    disabled={
                      order.orderStatusID === 4 || order.orderStatusID === 5
                    }
                  >
                    <option value="">Select State</option>
                    {states.map((state) => (
                      <option
                        key={state.abbreviation}
                        value={state.abbreviation}
                      >
                        {state.name} ({state.abbreviation})
                      </option>
                    ))}
                  </select>
                </div>
                {order.candidateState === "INT" && (
                  <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                    <label className="field-label">Country:</label>
                    <div className="field-input">
                      <input
                        className="form-control"
                        type="text"
                        value={
                          order.candidateCountry ? order.candidateCountry : ""
                        }
                        onChange={(e) =>
                          handleInputChange(e, "candidateCountry")
                        }
                        disabled={
                          order.orderStatusID === 4 || order.orderStatusID === 5
                        }
                      />
                    </div>
                  </div>
                )}
                <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                  <label className="field-label">Zip Code:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={
                        order.candidateZipCode ? order.candidateZipCode : ""
                      }
                      onChange={(e) => handleInputChange(e, "candidateZipCode")}
                      disabled={
                        order.orderStatusID === 4 || order.orderStatusID === 5
                      }
                    />
                  </div>
                </div>

                {additionalNames?.length !== 0 ? (
                  <div>
                    <div
                      className="highlight-text text-container-header"
                      style={{ flex: "0 0 100%", marginBottom: "10px" }}
                    >
                      Additional Names
                    </div>
                    <br></br>
                    {additionalNames?.map((name, index) => (
                      <div
                        key={name.additionalNameID}
                        className="additional-contact-group"
                      >
                        <div
                          className="field-stacked"
                          style={{ flex: "0 0 20%" }}
                        >
                          <label className="field-label">First Name:</label>
                          <div className="field-input">
                            <input
                              className={`form-control`}
                              type="text"
                              value={name.firstName || ""}
                              onChange={(e) =>
                                handleAdditionalNameChange(
                                  e,
                                  "firstName",
                                  index,
                                )
                              }
                              disabled={
                                order.orderStatusID === 4 ||
                                order.orderStatusID === 5
                              }
                            />
                          </div>
                        </div>
                        <div
                          className="field-stacked"
                          style={{ flex: "0 0 15%" }}
                        >
                          <label className="field-label">Middle Name:</label>
                          <div className="field-input">
                            <input
                              className={`form-control`}
                              type="text"
                              value={name.middleName || ""}
                              onChange={(e) =>
                                handleAdditionalNameChange(
                                  e,
                                  "middleName",
                                  index,
                                )
                              }
                              disabled={
                                order.orderStatusID === 4 ||
                                order.orderStatusID === 5
                              }
                            />
                          </div>
                        </div>
                        <div
                          className="field-stacked"
                          style={{ flex: "0 0 30%" }}
                        >
                          <label className="field-label">Last Name:</label>
                          <div className="field-input">
                            <input
                              className={`form-control`}
                              type="text"
                              value={name.lastName || ""}
                              onChange={(e) =>
                                handleAdditionalNameChange(e, "lastName", index)
                              }
                              disabled={
                                order.orderStatusID === 4 ||
                                order.orderStatusID === 5
                              }
                            />
                          </div>
                        </div>
                        <div
                          className="field-stacked"
                          style={{ flex: "0 0 30%" }}
                        >
                          <label className="field-label">From:</label>
                          <MonthDayYearPicker
                            value={getSafePickerDate(name.fromDate)}
                            onChange={(newDate) =>
                              handleAdditionalNameChange(
                                {
                                  target: { value: toSafeDateString(newDate) },
                                } as any,
                                "fromDate",
                                index,
                              )
                            }
                            disabled={
                              order.orderStatusID === 4 ||
                              order.orderStatusID === 5
                            }
                          />
                        </div>
                        <div
                          className="field-stacked"
                          style={{ flex: "0 0 30%" }}
                        >
                          <label className="field-label">To:</label>
                          <MonthDayYearPicker
                            value={getSafePickerDate(name.toDate)}
                            onChange={(newDate) =>
                              handleAdditionalNameChange(
                                {
                                  target: { value: toSafeDateString(newDate) },
                                } as any,
                                "toDate",
                                index,
                              )
                            }
                            disabled={
                              order.orderStatusID === 4 ||
                              order.orderStatusID === 5
                            }
                          />
                        </div>

                        {(order.orderStatusID === 1 ||
                          order.orderStatusID === 2 ||
                          order.orderStatusID === 3) && (
                          <>
                            <div
                              className="action-buttons"
                              style={{ justifyContent: "left" }}
                            >
                              <div>
                                <button
                                  className="smbtn btn-green"
                                  onClick={() =>
                                    handleUpdateAdditionalName(
                                      name.additionalNameID,
                                      name,
                                    )
                                  }
                                >
                                  Save
                                </button>
                              </div>
                              <div>
                                <button
                                  className="smbtn btn-red"
                                  onClick={() =>
                                    setDeletingNameID(name.additionalNameID)
                                  }
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                            {deletingNameID === name.additionalNameID && (
                              <ConfirmDelete
                                onConfirm={() => {
                                  handleDeleteAdditionalName(
                                    name.additionalNameID,
                                  );
                                  setDeletingNameID(null);
                                }}
                                onCancel={() => setDeletingNameID(null)}
                                message="Are you sure you want to delete this additional name?"
                              />
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div></div>
                )}
                {(order.orderStatusID === 1 ||
                  order.orderStatusID === 2 ||
                  order.orderStatusID === 3) && (
                  <div style={{ flex: "0 0 100%" }}>
                    <button
                      className="smbtn btn-purple"
                      onClick={() => setShowAddAdditionalName(true)}
                    >
                      Add Additional Name
                      <i className="bi bi-plus-lg btn-icon"></i>
                    </button>
                  </div>
                )}
                {showAddAdditionalName && (
                  <AddAdditionalName
                    orderID={order.orderID}
                    onClose={() => {
                      setShowAddAdditionalName(false);
                      window.location.reload();
                    }}
                  />
                )}

                <div
                  className="page-items-container"
                  style={{ flex: "0 0 100%", paddingTop: "20px" }}
                >
                  <div className="text-container-header">Order</div>
                </div>

                <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                  <label className="field-label">Initiated Date:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="date"
                      value={formatDate(order.initiatedDate)}
                      onChange={(e) => handleInputChange(e, "initiatedDate")}
                      disabled
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                  <div style={{ display: "flex", gap: "5px" }}>
                    <label className="field-label">Initial Forms Date:</label>
                    <ToolTip
                      tooltipText="The date the forms were sent/became available to the candidate."
                      placement="right"
                      className="info-icon-spec"
                    ></ToolTip>
                  </div>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="date"
                      value={formatDate(order.initialFormsDate)}
                      onChange={(e) => handleInputChange(e, "initialFormsDate")}
                      disabled={
                        order.orderStatusID === 4 || order.orderStatusID === 5
                      }
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                  <div style={{ display: "flex", gap: "5px" }}>
                    <label className="field-label">Received Date:</label>
                    <ToolTip
                      tooltipText="The date the forms were returned by the candidate."
                      placement="right"
                      className="info-icon-spec"
                    ></ToolTip>
                  </div>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="date"
                      value={formatDate(order.receivedDate)}
                      onChange={(e) => handleInputChange(e, "receivedDate")}
                      disabled={
                        order.orderStatusID === 4 || order.orderStatusID === 5
                      }
                    />
                  </div>
                </div>
                {criminalOrder && criminalOrder.criminalOrderID !== 0 && (
                  <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                    <label className="field-label">
                      Criminal Final Report:
                    </label>
                    <div className="field-input">
                      <input
                        className="form-control"
                        type="date"
                        value={formatDate(criminalOrder?.finalReportDate)}
                        disabled
                      />
                    </div>
                  </div>
                )}
                {referenceOrder && referenceOrder.referenceOrderID !== 0 && (
                  <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                    <label className="field-label">
                      Reference Final Report:
                    </label>
                    <div className="field-input">
                      <input
                        className="form-control"
                        type="date"
                        value={formatDate(referenceOrder.finalReportDate)}
                        disabled
                      />
                    </div>
                  </div>
                )}
                <div
                  className="field-stacked"
                  style={{ flex: "0 0 100%" }}
                ></div>
                <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                  <label className="field-label">Order ID:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={order.orderID}
                      disabled
                    />
                  </div>
                </div>

                <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                  <label className="field-label">Sent By Mail:</label>
                  <div>
                    <input
                      className={`form-check-input`}
                      type="checkbox"
                      checked={order.sendByMail}
                      onChange={(e) => handleInputChange(e, "sendByMail")}
                      disabled
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                  <label className="field-label">FCRA Compliance:</label>
                  <div>
                    <input
                      className={`form-check-input`}
                      type="checkbox"
                      checked={order.fcraCompliance}
                      onChange={(e) => handleInputChange(e, "fcraCompliance")}
                      disabled
                    />
                  </div>
                </div>
                <div style={{ flex: "0 0 80%" }}></div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">Invoice Process Type:</label>
                  <div
                    style={{
                      flex: "0 0 30%",
                      alignSelf: "end",
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <select
                      className={`form-select`}
                      value={order.invoiceProcessTypeID || ""}
                      onChange={(e) =>
                        handleInputChange(e, "invoiceProcessTypeID")
                      }
                      disabled={
                        order.orderStatusID === 4 || order.orderStatusID === 5
                      }
                    >
                      {invoiceProcessTypes.map((type) => (
                        <option
                          key={type.invoiceProcessTypeID}
                          value={type.invoiceProcessTypeID}
                        >
                          {type.description}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 60%" }}>
                  <label className="field-label">Designation:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={order.designation ? order.designation : ""}
                      onChange={(e) => handleInputChange(e, "designation")}
                      disabled={
                        order.orderStatusID === 4 || order.orderStatusID === 5
                      }
                    />
                  </div>
                </div>
                <div style={{ flex: "0 0 100%" }}></div>

                {referenceOrder?.referenceOrderID !== 0 && referenceOrder && (
                  <>
                    {" "}
                    <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                      <label className="field-label">Reference Type:</label>
                      <div
                        style={{
                          flex: "0 0 30%",
                          alignSelf: "end",
                          display: "flex",
                          flexDirection: "row",
                        }}
                      >
                        <select
                          className={`form-select`}
                          value={referenceOrder.referenceTypeID || ""}
                          onChange={(e) =>
                            handleInputChange(e, "referenceTypeID")
                          }
                          disabled={
                            order.orderStatusID === 4 ||
                            order.orderStatusID === 5
                          }
                        >
                          {referenceTypes.map((type) => (
                            <option
                              key={type.referenceTypeID}
                              value={type.referenceTypeID}
                            >
                              {type.description}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div
                      className="field-stacked"
                      style={{ flex: "0 0 70%" }}
                    ></div>
                    {referenceOrder.referenceTypeID === 1 && (
                      <>
                        <div
                          className="field-stacked"
                          style={{ flex: "0 0 40%" }}
                        >
                          <label className="field-label">
                            Prior Client Name:
                          </label>
                          <div className="field-input">
                            <input
                              className="form-control"
                              type="text"
                              value={
                                referenceOrder.priorClientName
                                  ? referenceOrder.priorClientName
                                  : ""
                              }
                              onChange={(e) =>
                                handleInputChange(e, "priorClientName")
                              }
                              disabled={
                                order.orderStatusID === 4 ||
                                order.orderStatusID === 5
                              }
                            />
                          </div>
                        </div>
                        <div
                          className="field-stacked"
                          style={{ flex: "0 0 30%" }}
                        >
                          <label className="field-label">Prior Date:</label>
                          <div className="field-input">
                            <MonthDayYearPicker
                              value={getSafePickerDate(
                                referenceOrder.priorDate,
                              )}
                              onChange={(newDate) =>
                                handleInputChange(
                                  {
                                    target: {
                                      value: toSafeDateString(newDate),
                                    },
                                  } as any,
                                  "priorDate",
                                )
                              }
                              disabled={
                                order.orderStatusID === 4 ||
                                order.orderStatusID === 5
                              }
                            />
                          </div>
                        </div>
                        <div
                          className="field-stacked"
                          style={{ flex: "0 0 10%" }}
                        >
                          <label className="field-label">
                            Response Tracking Received:
                          </label>
                          <div className="field-input">
                            <input
                              className="form-control"
                              type="text"
                              value={
                                referenceOrder.responseTrackingReceived
                                  ? referenceOrder.responseTrackingReceived
                                  : ""
                              }
                              onChange={(e) =>
                                handleInputChange(e, "responseTrackingReceived")
                              }
                              disabled={
                                order.orderStatusID === 4 ||
                                order.orderStatusID === 5
                              }
                            />
                          </div>
                        </div>

                        {/* Client Responses */}
                        <div style={{ display: "flex", gap: "5px" }}>
                          <button
                            className="smbtn btn-grey"
                            onClick={() => setShowUploadResponsePopup(true)}
                          >
                            Upload Client Response
                          </button>
                          <ToolTip
                            tooltipText="Use a unique file name to prevent overwriting."
                            placement="right"
                            className="info-icon-spec"
                          ></ToolTip>
                        </div>
                        <div>
                          <button
                            className={`smbtn btn-grey`}
                            onClick={() => setShowDownloadResponsePopup(true)}
                          >
                            Download Client Response
                          </button>
                        </div>

                        {showUploadResponsePopup && (
                          <UploadClientResponse
                            order={order}
                            onClose={() => {
                              setShowUploadResponsePopup(false);
                            }}
                          />
                        )}
                        {showDownloadResponsePopup && client && (
                          <DownloadClientResponse
                            order={order}
                            onClose={() => {
                              setShowDownloadResponsePopup(false);
                            }}
                          />
                        )}
                      </>
                    )}
                    {referenceOrder.referenceTypeID === 2 && (
                      <>
                        <div
                          className="field-stacked"
                          style={{ flex: "0 0 10%" }}
                        >
                          <label className="field-label">
                            Response Tracking Received:
                          </label>
                          <div className="field-input">
                            <input
                              className="form-control"
                              type="text"
                              value={
                                referenceOrder.responseTrackingReceived
                                  ? referenceOrder.responseTrackingReceived
                                  : ""
                              }
                              onChange={(e) =>
                                handleInputChange(e, "responseTrackingReceived")
                              }
                              disabled={
                                order.orderStatusID === 4 ||
                                order.orderStatusID === 5
                              }
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}

                <div style={{ display: "flex", flex: "0 0 100%" }}>
                  <div>
                    <button
                      className={`smbtn btn-grey`}
                      onClick={() => setShowUploadGeneralOrderDocPopup(true)}
                    >
                      General Document Upload
                    </button>
                  </div>
                  <div>
                    <button
                      style={{ marginLeft: "20px" }}
                      className={`smbtn btn-grey`}
                      onClick={() => setShowDownloadGeneralOrderDocsPopup(true)}
                    >
                      Download Document
                    </button>
                  </div>
                  {orderDocCount > 0 && (
                    <div style={{ display: "flex", alignSelf: "start" }}>
                      <span className="doc-count-dot">{orderDocCount}</span>
                    </div>
                  )}
                </div>

                {showUploadGeneralOrderDocPopup && (
                  <UploadGeneralOrderDoc
                    order={order}
                    onClose={() => {
                      setShowUploadGeneralOrderDocPopup(false);
                      updateDocCount();
                    }}
                  />
                )}
                {showDownloadGeneralOrderDocsPopup && order && (
                  <DownloadGeneralOrderDocs
                    order={order}
                    onClose={() => {
                      setShowDownloadGeneralOrderDocsPopup(false);
                      updateDocCount();
                    }}
                  />
                )}

                <div
                  className="text-container-header"
                  style={{ flex: "0 0 100%" }}
                >
                  Comments
                </div>
                <div style={{ flex: "0 0 100%" }}>
                  <textarea
                    className="form-control"
                    value={order.comments ? order.comments : ""}
                    onChange={(e) => handleInputChange(e, "comments")}
                    style={{ height: "100px" }}
                    maxLength={4000}
                  />
                  <div
                    style={{
                      fontSize: "14px",
                      marginTop: "4px",
                      textAlign: "right",
                    }}
                  >
                    {order.comments?.length ?? 0}/4000
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="data-container">
            {client?.requireNotary &&
              referenceOrder !== null &&
              referenceOrder?.referenceOrderID !== 0 && (
                <div className="error">
                  Confirm the candidate questionnaire includes the notary
                  requirement.
                </div>
              )}
            <div
              className="text-container-header"
              style={{ flex: "0 0 100%", display: "flex", flexWrap: "wrap" }}
            >
              Candidate Forms
              <div>
                <button
                  style={{ marginLeft: "20px" }}
                  className={`smbtn btn-grey ${
                    order.orderStatusID === 4 || order.orderStatusID === 5
                      ? "disabled"
                      : ""
                  }`}
                  onClick={() => setShowUploadCandidateFormPopup(true)}
                  disabled={
                    order.orderStatusID === 4 || order.orderStatusID === 5
                  }
                >
                  Upload Candidate Form
                </button>
              </div>
              <div>
                <button
                  style={{ marginLeft: "20px" }}
                  className={`smbtn btn-grey ${
                    order.orderStatusID === 5 ? "disabled" : ""
                  }`}
                  onClick={() => setShowDownloadCandidateFormPopup(true)}
                  disabled={order.orderStatusID === 5}
                >
                  Download Candidate Form
                </button>
              </div>
              {referenceOrder && referenceOrder.referenceOrderID !== 0 && (
                <div style={{ display: "flex", gap: "10px" }}>
                  <div>
                    <button
                      style={{ marginLeft: "20px" }}
                      className={`smbtn btn-grey ${
                        order.orderStatusID === 5 ||
                        referenceOrder.clientNotifiedDate !== null
                          ? "disabled"
                          : ""
                      }`}
                      onClick={() =>
                        setShowNotifyClientPopup(!showNotifyClientPopup)
                      }
                      disabled={
                        order.orderStatusID === 5 ||
                        referenceOrder.clientNotifiedDate !== null
                      }
                    >
                      Notify Client <i className="btn-icon bi bi-envelope"></i>
                    </button>
                  </div>
                  <ToolTip
                    tooltipText="Notify the Client that a yes response has been found on the candidate questionnaire."
                    placement="right"
                    className="info-icon-spec"
                  ></ToolTip>
                </div>
              )}
              {referenceOrder?.clientNotifiedDate !== null && (
                <>
                  <button
                    className="smbtn btn-grey"
                    style={{
                      cursor: "default",
                      background: "none",
                      color: "black",
                    }}
                  >
                    {formatDate(referenceOrder?.clientNotifiedDate, "us")}
                  </button>
                  <div
                    className="field-long smbtn btn-grey"
                    style={{
                      flex: "0 0 10%",
                      cursor: "default",
                      background: "none",
                      color: "black",
                    }}
                  >
                    <div style={{ display: "flex", gap: "5px" }}>
                      <label className="field-label">Red Flag:</label>
                      <ToolTip
                        tooltipText="This flag is set by the system if a candidate questionnaire is submitted containing a 'yes', but can also be manually set by admin."
                        placement="top"
                        className="info-icon-spec"
                      ></ToolTip>
                    </div>
                    <div>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={
                          referenceOrder?.canQCrimAdmit
                            ? referenceOrder.canQCrimAdmit
                            : false
                        }
                        onChange={(e) => handleInputChange(e, "canQCrimAdmit")}
                      />
                    </div>
                  </div>
                  <div
                    className="field-long smbtn btn-grey"
                    style={{
                      flex: "0 0 10%",
                      cursor: "default",
                      background: "none",
                      color: "black",
                    }}
                  >
                    <div style={{ display: "flex", gap: "5px" }}>
                      <label className="field-label">Retract:</label>
                      <ToolTip
                        tooltipText="Even though client was notified, don't include this contact on the final report."
                        placement="right"
                        className="info-icon-spec"
                      ></ToolTip>
                    </div>
                    <div>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={
                          referenceOrder?.retract
                            ? referenceOrder?.retract
                            : false
                        }
                        onChange={(e) => handleInputChange(e, "retract")}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            {showUploadCandidateFormPopup && (
              <UploadCandidateForm
                onClose={() => {
                  setShowUploadCandidateFormPopup(false);
                }}
              />
            )}
            {showDownloadCandidateFormPopup && (
              <DownloadCandidateForm
                onClose={() => {
                  setShowDownloadCandidateFormPopup(false);
                }}
              />
            )}
            {showNotifyClientPopup && referenceOrder && (
              <NotifyClient
                orderId={order.orderID}
                clientId={order.clientID}
                candidateEmail={order.candidateEmail ?? ""}
                type={NotificationType.Candidate}
                onClose={handleNotifyClientClosePopup}
              />
            )}
            {renderFormButtons()}
            {deleteTarget && (
              <ConfirmDelete
                onConfirm={() => {
                  handleDeleteOrderForm();
                  setDeleteTarget(null);
                }}
                message="Delete Form/Requirement"
                onCancel={() => setDeleteTarget(null)}
              />
            )}
            <div>
              <button
                style={{ marginLeft: "20px" }}
                className={`smbtn btn-purple ${
                  order.orderStatusID === 5 ? "disabled" : ""
                }`}
                onClick={() => setShowAddOrderFormPopup(true)}
                disabled={order.orderStatusID === 5}
              >
                Add Required Form <i className="bi bi-plus-lg btn-icon"></i>
              </button>
            </div>
          </div>

          {showAddOrderFormPopup && (
            <AddRequiredForm
              onClose={() => {
                setShowAddOrderFormPopup(false);
              }}
            />
          )}

          {criminalOrder && criminalOrder.criminalOrderID !== 0 ? (
            <CriminalOrderSection
              handleInputChange={handleInputChange}
              getServiceNames={getServiceNames}
            />
          ) : (
            <div></div>
            // <div style={{ flex: "0 0 100%" }}>
            //   <button
            //     className={`smbtn btn-purple`}
            //     onClick={() => handleCreateCriminalOrder()}
            //     style={{ marginBottom: "30px" }}
            //   >
            //     Create Criminal Order <i className="bi bi-plus-lg btn-icon"></i>
            //   </button>
            // </div>
          )}

          {subscriptionOrder && subscriptionOrder.subscriptionOrderID !== 0 ? (
            <SubscriptionOrderSection
              handleInputChange={handleInputChange}
              getServiceNames={getServiceNames}
            />
          ) : (
            <div></div>
            // <div style={{ flex: "0 0 100%" }}>
            //   <button
            //     className={`smbtn btn-purple`}
            //     onClick={() => handleCreateSubscriptionOrder()}
            //     style={{ marginBottom: "30px" }}
            //   >
            //     Create Subscription Order{" "}
            //     <i className="bi bi-plus-lg btn-icon"></i>
            //   </button>
            // </div>
          )}

          {referenceOrder && referenceOrder.referenceOrderID !== 0 ? (
            <ReferenceOrderSection
              handleInputChange={handleInputChange}
              getServiceNames={getServiceNames}
            />
          ) : (
            <div></div>
            // <div style={{ flex: "0 0 100%" }}>
            //   <button
            //     className={`smbtn btn-purple`}
            //     onClick={() => handleCreateReferenceOrder()}
            //     style={{ marginBottom: "30px" }}
            //   >
            //     Create Reference Order{" "}
            //     <i className="bi bi-plus-lg btn-icon"></i>
            //   </button>
            // </div>
          )}

          {successMessage && (
            <div className="alert-message alert alert-success alert-dismissible fade show">
              {successMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}

          <div className="action-buttons">
            <button
              className={`oxbtn btn-green ${!editing ? "disabled" : ""}`}
              onClick={() => handleSave()}
              disabled={!editing}
            >
              Save
            </button>
            {order.orderStatusID !== 4 && order.orderStatusID !== 5 && (
              <div>
                <button
                  style={{ marginRight: "15px" }}
                  className={`oxbtn btn-purple ${
                    completedDisabled ? "disabled" : ""
                  }`}
                  onClick={() => setShowCompleteOrder(true)}
                  disabled={completedDisabled}
                >
                  <div> Complete Order </div>
                </button>
                {showCompleteOrder && (
                  <CompleteOrder
                    order={order}
                    onClose={() => {
                      setShowCompleteOrder(false);
                      window.location.reload();
                    }}
                  />
                )}
                <button
                  className={`oxbtn btn-red ${
                    deleteDisabled ? "disabled" : ""
                  }`}
                  onClick={() => setShowDeleteOrder(true)}
                  disabled={deleteDisabled}
                >
                  <div> Delete Order </div>
                </button>
                {showDeleteOrder && (
                  <DeleteOrder
                    order={order}
                    onClose={() => {
                      setShowDeleteOrder(false);
                    }}
                    onSuccess={() => navigate("/admin/orders/deleted-orders")}
                  />
                )}
              </div>
            )}
            {order.orderStatusID === 5 && (
              <div>
                <button
                  className={`oxbtn btn-red`}
                  onClick={() => setShowHardDeleteOrder(true)}
                >
                  Hard Delete
                </button>
                {showHardDeleteOrder && (
                  <HardDeleteOrder
                    order={order}
                    onClose={() => {
                      setShowHardDeleteOrder(false);
                    }}
                    onSuccess={() => () => navigate("/orders/all-orders")}
                  />
                )}
              </div>
            )}
          </div>
        </>
      )}
      {successMessage && (
        <div className="alert-message alert alert-success alert-dismissible fade show">
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
      {errorMessage && (
        <div
          className="alert-message alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
    </div>
  );
};

export default AdminViewOrder;
